class CustomScrollbar extends HTMLElement {
  connectedCallback() {
    const targetSelector = this.getAttribute("data-target");
    this.scrollable = targetSelector
      ? document.querySelector(targetSelector)
      : this.previousElementSibling;

    if (!this.scrollable) return;

    this.track = document.createElement("div");
    this.track.className = "custom-scrollbar__track";
    this.thumb = document.createElement("div");
    this.thumb.className = "custom-scrollbar__thumb";
    this.track.appendChild(this.thumb);
    this.appendChild(this.track);

    this.update = this.update.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onThumbDown = this.onThumbDown.bind(this);
    this.onThumbMove = this.onThumbMove.bind(this);
    this.onThumbUp = this.onThumbUp.bind(this);
    this.onTrackPointerDown = this.onTrackPointerDown.bind(this);

    this.scrollable.addEventListener("scroll", this.onScroll, {
      passive: true,
    });
    this.scrollable.addEventListener("scrollend", this.onScrollEnd, {
      passive: true,
    });
    this.thumb.addEventListener("mousedown", this.onThumbDown);
    this.thumb.addEventListener("touchstart", this.onThumbDown, {
      passive: false,
    });
    this.track.addEventListener("mousedown", this.onTrackPointerDown);

    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.scrollable);
    this.resizeObserver.observe(this.track);

    this.mutationObserver = new MutationObserver(this.update);
    this.mutationObserver.observe(this.scrollable, {
      childList: true,
      subtree: true,
    });

    this.update();
  }

  disconnectedCallback() {
    this.scrollable?.removeEventListener("scroll", this.onScroll);
    this.scrollable?.removeEventListener("scrollend", this.onScrollEnd);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    document.removeEventListener("mousemove", this.onThumbMove);
    document.removeEventListener("mouseup", this.onThumbUp);
    document.removeEventListener("touchmove", this.onThumbMove);
    document.removeEventListener("touchend", this.onThumbUp);
  }

  update() {
    if (!this.scrollable || !this.track || !this.thumb) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollable;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 1) {
      this.style.visibility = "hidden";
      return;
    }
    this.style.visibility = "";

    const effectiveMax = this.computeEffectiveMax(maxScroll);
    const trackWidth = this.track.clientWidth;
    const thumbWidth = this.thumb.offsetWidth;
    const maxThumbX = Math.max(0, trackWidth - thumbWidth);
    const ratio = effectiveMax > 0 ? Math.min(1, scrollLeft / effectiveMax) : 0;
    const thumbX = ratio * maxThumbX;

    this.thumb.style.transform = `translateX(${thumbX}px)`;
  }

  // With scroll-snap-type: mandatory + scroll-snap-align: start, the last
  // reachable scrollLeft is the largest child.offsetLeft that's still <=
  // maxScroll. Children further right can't snap-align-to-start because
  // there isn't enough room left to scroll.
  computeEffectiveMax(maxScroll) {
    const children = this.scrollable.children;
    let lastReachable = 0;
    for (let i = 0; i < children.length; i++) {
      const offset = children[i].offsetLeft;
      if (offset <= maxScroll && offset > lastReachable) {
        lastReachable = offset;
      }
    }
    return lastReachable > 0 ? lastReachable : maxScroll;
  }

  onScroll() {
    if (this.dragging) return;
    this.update();

    this.track.classList.add("is-scrolling");
  }

  onScrollEnd() {
    this.track.classList.remove("is-scrolling");
  }

  onThumbDown(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this.thumb.classList.add("is-dragging");

    const point = this.getPoint(event);
    const thumbRect = this.thumb.getBoundingClientRect();
    this.dragOffset = point.x - thumbRect.left;

    document.addEventListener("mousemove", this.onThumbMove);
    document.addEventListener("mouseup", this.onThumbUp);
    document.addEventListener("touchmove", this.onThumbMove, {
      passive: false,
    });
    document.addEventListener("touchend", this.onThumbUp);
  }

  onThumbMove(event) {
    if (!this.dragging) return;
    event.preventDefault();

    const point = this.getPoint(event);
    this.applyThumbPosition(point.x - this.dragOffset);
  }

  onThumbUp() {
    this.dragging = false;
    this.thumb.classList.remove("is-dragging");
    document.removeEventListener("mousemove", this.onThumbMove);
    document.removeEventListener("mouseup", this.onThumbUp);
    document.removeEventListener("touchmove", this.onThumbMove);
    document.removeEventListener("touchend", this.onThumbUp);
  }

  onTrackPointerDown(event) {
    if (event.target === this.thumb) return;

    const thumbWidth = this.thumb.offsetWidth;
    this.applyThumbPosition(event.clientX - thumbWidth / 2, true);
  }

  applyThumbPosition(clientX, smooth = false) {
    const trackRect = this.track.getBoundingClientRect();
    const thumbWidth = this.thumb.offsetWidth;
    const maxThumbX = Math.max(0, trackRect.width - thumbWidth);

    let thumbX = clientX - trackRect.left;
    thumbX = Math.max(0, Math.min(thumbX, maxThumbX));

    const { scrollWidth, clientWidth } = this.scrollable;
    const maxScroll = scrollWidth - clientWidth;
    const effectiveMax = this.computeEffectiveMax(maxScroll);
    const targetScroll =
      maxThumbX > 0 ? (thumbX / maxThumbX) * effectiveMax : 0;

    if (smooth) {
      this.scrollable.scrollTo({ left: targetScroll, behavior: "smooth" });
    } else {
      this.scrollable.scrollLeft = targetScroll;
      this.thumb.style.transform = `translateX(${thumbX}px)`;
    }
  }

  getPoint(event) {
    if (event.touches && event.touches[0]) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  }
}

if (!customElements.get("custom-scrollbar")) {
  customElements.define("custom-scrollbar", CustomScrollbar);
}
